/** Client-side range filters on scan results (price EUR, km). */
export interface DealRangeFilters {
  priceMin: number | null;
  priceMax: number | null;
  kmMin: number | null;
  kmMax: number | null;
}

export const EMPTY_RANGE_FILTERS: DealRangeFilters = {
  priceMin: null,
  priceMax: null,
  kmMin: null,
  kmMax: null,
};

export function hasActiveRangeFilters(f: DealRangeFilters): boolean {
  return f.priceMin != null || f.priceMax != null || f.kmMin != null || f.kmMax != null;
}

export function applyDealRangeFilters<
  T extends { currentPriceEur?: number | null; currentKm?: number | null },
>(deals: T[], f: DealRangeFilters): T[] {
  if (!hasActiveRangeFilters(f)) return deals;
  return deals.filter((d) => {
    if (f.priceMin != null) {
      if (d.currentPriceEur == null || d.currentPriceEur < f.priceMin) return false;
    }
    if (f.priceMax != null) {
      if (d.currentPriceEur == null || d.currentPriceEur > f.priceMax) return false;
    }
    if (f.kmMin != null) {
      if (d.currentKm == null || d.currentKm < f.kmMin) return false;
    }
    if (f.kmMax != null) {
      if (d.currentKm == null || d.currentKm > f.kmMax) return false;
    }
    return true;
  });
}

export interface DealRangeBounds {
  priceMin: number;
  priceMax: number;
  kmMin: number;
  kmMax: number;
}

export function computeDealRangeBounds(
  deals: Array<{ currentPriceEur?: number | null; currentKm?: number | null }>,
): DealRangeBounds | null {
  let priceMin = Infinity;
  let priceMax = -Infinity;
  let kmMin = Infinity;
  let kmMax = -Infinity;
  let nPrice = 0;
  let nKm = 0;

  for (const d of deals) {
    if (d.currentPriceEur != null) {
      priceMin = Math.min(priceMin, d.currentPriceEur);
      priceMax = Math.max(priceMax, d.currentPriceEur);
      nPrice++;
    }
    if (d.currentKm != null && d.currentKm > 0) {
      kmMin = Math.min(kmMin, d.currentKm);
      kmMax = Math.max(kmMax, d.currentKm);
      nKm++;
    }
  }

  if (nPrice === 0 && nKm === 0) return null;
  return {
    priceMin: nPrice > 0 ? priceMin : 0,
    priceMax: nPrice > 0 ? priceMax : 0,
    kmMin: nKm > 0 ? kmMin : 0,
    kmMax: nKm > 0 ? kmMax : 0,
  };
}
